import * as React from 'react'
import {FC} from 'react'
import {Transition} from 'react-transition-group'
import {observer} from 'mobx-react'
import cn from 'classnames'
import {PaymentType, RecurringInterval} from '@raisenow/epik'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {DynamicSlotName} from '../../../../lib/DynamicSlotName'
import {WidgetEventName} from '../../../../lib/WidgetEventer'
import {DynamicSlot} from '../../../dynamic/DynamicSlot'
import {InfoToggle} from '../../../ui/InfoToggle'
import {InputRadioWrapper} from '../../../wrappers/InputCheckableWrapper'
import {DirtyWrapper} from '../../../wrappers/DirtyWrapper'
import {RippleWrapper} from '../../../wrappers/RippleWrapper'
import {buildSlideTransitionProps} from '../../../../lib/helpers'

///////////////////////////////////////////////////////////////////////////////

export const PaymentRecurringIntervalsSimple: FC = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const form = api.paymentForm
  const {uiTransitionTimeout: timeout} = api.computedConfig
  const slideTransitionProps = buildSlideTransitionProps({timeout})

  const isRecurring = form.getPaymentType() === PaymentType.RECURRING
  const recurringIntervals = form.getPossibleRecurringIntervals()

  const currentPaymentType = form.getPaymentType()
  const isCurrentPaymentTypeOneTime =
    currentPaymentType === PaymentType.ONE_TIME
  const isCurrentPaymentTypeRecurring =
    currentPaymentType === PaymentType.RECURRING

  const infoToggleTitle = api.trans('info_toggle.recurring_intervals.title', {
    defaultValue: null,
  })
  const infoToggleTextHTML = api.trans(
    'info_toggle.recurring_intervals.text_html',
    {defaultValue: null},
  )

  const selectOption = (interval: RecurringInterval | PaymentType) => {
    setTimeout(async () => {
      if (recurringIntervals.includes(interval as RecurringInterval)) {
        form.setPaymentType(PaymentType.RECURRING)
        form.setRecurringInterval(interval as RecurringInterval)
        await api.events[WidgetEventName.RECURRING_INTERVAL_CHANGED].publish({
          api,
        })
      } else {
        form.setPaymentType(PaymentType.ONE_TIME)
      }

      await api.events[WidgetEventName.PAYMENT_TYPE_CHANGED].publish({api})
    })
  }

  return (
    <div className="payment-recurring-intervals-simple">
      <DynamicSlot
        name={DynamicSlotName.RECURRING_INTERVALS_START}
        form={form}
      />

      <fieldset className="main">
        <legend>
          {api.trans(
            'blocks.payment_amounts_and_intervals.recurring_intervals.legend',
          )}
        </legend>

        <div className="payment-intervals">
          {[PaymentType.ONE_TIME, ...recurringIntervals].map((option, key) => {
            let isChecked

            if (option === PaymentType.ONE_TIME) {
              isChecked =
                isCurrentPaymentTypeOneTime && option === PaymentType.ONE_TIME
            } else {
              isChecked =
                isCurrentPaymentTypeRecurring &&
                option === form.getRecurringInterval()
            }

            return (
              <RippleWrapper key={key}>
                <label
                  key={key}
                  className={cn([
                    'label-radio-block',
                    'payment-interval',
                    {selected: isChecked},
                  ])}
                >
                  <div className="main">
                    <InputRadioWrapper innerStyle="tick">
                      <DirtyWrapper form={form} fields="recurring_interval">
                        <input
                          type="radio"
                          className="input-radio"
                          name="recurring_interval"
                          value={option}
                          onChange={() => selectOption(option)}
                          checked={isChecked}
                        />
                      </DirtyWrapper>
                    </InputRadioWrapper>
                    <span className="title">
                      {api.trans(`recurring_intervals.${option}`)}
                    </span>
                  </div>
                </label>
              </RippleWrapper>
            )
          })}
        </div>
      </fieldset>

      <Transition
        in={isRecurring}
        mountOnEnter
        unmountOnExit
        {...slideTransitionProps}
      >
        <InfoToggle
          idKey="info-toggle-recurring-intervals"
          className="info-toggle-recurring-intervals"
          title={infoToggleTitle}
          textHTML={infoToggleTextHTML}
        />
      </Transition>

      <DynamicSlot name={DynamicSlotName.RECURRING_INTERVALS_END} form={form} />
    </div>
  )
})
